// ============================================
// COMPANYDETAILS.JSX - Individual Company Details Page
// ============================================

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCompanyMembersQuery,
  useGetCompanyByIdQuery,
} from "../../../services/api";
import EmployeesSection from "./components/EmployeesSection";
import NavigationCard from "./components/NavigationCard";
import "./CompanyDetails.css";

// Icons for navigation cards
const IconCompetencies = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const IconValues = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const IconDiagnostic = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const IconSurvey = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <line x1="9" y1="10" x2="15" y2="10" />
    <line x1="9" y1="14" x2="15" y2="14" />
    <line x1="9" y1="18" x2="13" y2="18" />
  </svg>
);

/**
 * CompanyDetails Component
 * Main page for viewing company overview with navigation to sub-sections
 */
export default function CompanyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ==================== PAGINATION STATE ====================
  const [employeesPage, setEmployeesPage] = useState(1);
  const employeesLimit = 10;

  // ==================== API QUERIES ====================
  const { data: membersData, isLoading: membersLoading } = useGetCompanyMembersQuery({
    id,
    page: employeesPage,
    limit: employeesLimit,
  });
  const employees = membersData?.members || [];
  const totalEmployeesPages = membersData?.totalPages || 1;

  const { data: companyData } = useGetCompanyByIdQuery(id);

  // ==================== COMPUTED VALUES ====================
  const companyName = companyData?.name || "Loading...";

  // ==================== RENDER ====================
  return (
    <div className="cd-company-details">
      <div className="welcome" onClick={() => navigate("/admin/companies")}>
        ← Back To Companies
      </div>
      <h1 className="username">{companyName}</h1>

      {/* Navigation Cards */}
      <div className="cd-section-title-main" style={{ marginBottom: "16px" }}>
        Manage Company
      </div>
      <div className="cd-diagnostic-grid" style={{ marginBottom: "32px" }}>
        <NavigationCard
          title="Competencies"
          description="Assign competency sets and manage rubrics for this company"
          icon={<IconCompetencies />}
          onClick={() => navigate(`/admin/companies/${id}/competencies`)}
        />
        <NavigationCard
          title="Unpack Values"
          description="Create and assign value forms, view employee responses"
          icon={<IconValues />}
          onClick={() => navigate(`/admin/companies/${id}/unpack-values`)}
        />
        <NavigationCard
          title="Diagnostic Forms"
          description="Create diagnostic assessments and track employee progress"
          icon={<IconDiagnostic />}
          onClick={() => navigate(`/admin/companies/${id}/diagnostic-forms`)}
        />
        <NavigationCard
          title="Surveys"
          description="Create surveys and view employee responses"
          icon={<IconSurvey />}
          onClick={() => navigate(`/admin/companies/${id}/surveys`)}
        />
      </div>

      {/* Employees Section */}
      <EmployeesSection
        employees={employees}
        currentPage={employeesPage}
        totalPages={totalEmployeesPages}
        onPageChange={setEmployeesPage}
        isLoading={membersLoading}
      />
    </div>
  );
}
